// Settings › Campaigns › Spend and conversions, Blueprint (BL-091; pages/ux-full screens/ads.js, the
// setup/ads screen's ads.conversions row): the switch that sends what came from Google ads to Google Ads
// as conversions (adConversions.setOn, from a first day), the map of each kind to its own conversion
// action (the ads.conversionActions setting), and how the ad accounts are doing (adAccounts.status,
// drawn by ui-web's AdsHealth). Its own part of the ad spend's section, mounted from AdSpend.tsx with
// one line; Riverstone never draws it.

import { useEffect, useState } from 'react';
import { RpcError } from '@flowology/client';
import { localDateOf } from '@flowology/kernel';
import { AdsHealth, AdsStatusProps } from '@flowology/ui-web';
import '@flowology/ui-web/base.css';
import { client } from '../api.ts';
import { officeHost } from '../blueprint/host.ts';
import { SaveButton } from '../shared/SaveButton.tsx';
import './conversions.css';
import {
  draftOf,
  KIND_WORDS,
  KINDS,
  mapFromWords,
  mapOf,
  switchWords,
  type MapDraft,
} from './conversions-model.ts';

const say = (e: unknown) => (e instanceof RpcError ? e.message : 'Could not save. Try again.');
const host = officeHost(() => undefined);

export function ConversionsPart({ canEdit }: { canEdit: boolean }) {
  const [status, setStatus] = useState<AdsStatusProps | null>(null);
  const [again, setAgain] = useState(0);
  const [first, setFirst] = useState('');
  const [adtrackOff, setAdtrackOff] = useState(false);
  const [draft, setDraft] = useState<MapDraft | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [said, setSaid] = useState<string | null>(null);
  useEffect(() => {
    client
      .call<unknown>('adAccounts.status', {})
      .then((r) => {
        const p = AdsStatusProps.safeParse(r);
        if (!p.success) throw new Error('Flowology can’t show the ad accounts here.');
        setStatus(p.data);
        setFirst((f) => f || localDateOf(new Date(), p.data.timeZone));
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : say(e)));
  }, [again]);
  const write = async (run: () => Promise<string>) => {
    setBusy(true);
    setError(null);
    try {
      setSaid(await run());
      setDraft(null);
      setAdtrackOff(false);
      setAgain((n) => n + 1);
    } catch (e) {
      setError(e instanceof Error ? e.message : say(e));
    } finally {
      setBusy(false);
    }
  };
  const c = status?.conversions;
  const saveMap = (d: MapDraft) =>
    write(async () => {
      const m = mapOf(d);
      if ('error' in m) throw new Error(m.error);
      await client.call('settings.set', { key: 'ads.conversionActions', value: m.value });
      return 'Conversion actions saved.';
    });

  return (
    <div className="bp-part conversions-part" role="group" aria-label="Conversions to Google Ads">
      <h3>Conversions to Google Ads</h3>
      <p className="muted">
        What came from a Google ad goes back to Google Ads, so its bidding learns which clicks and calls
        became paid work. One system sends them: turn AdTrack’s upload off before turning this on.
      </p>
      {said && (
        <p className="notice ok" role="status">
          {said}
        </p>
      )}
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      {!status && !error && <p className="muted">Getting the ad accounts…</p>}
      {c && (
        <ul className="rules">
          <li className="rule">
            <div className="what">
              <b>Send conversions to Google Ads</b>
              <p className="muted small">{switchWords(c.on, c.from)}</p>
              {!c.on && (
                <p className="muted small">
                  Pick the day after AdTrack’s last upload, so no day goes twice. Each job goes to an action
                  once, and a click AdTrack already sent is kept back.
                </p>
              )}
            </div>
            {!canEdit ? (
              <span className="ro-val">{c.on ? 'On' : 'Off'}</span>
            ) : c.on ? (
              <button
                type="button"
                className="btn"
                disabled={busy}
                onClick={() =>
                  void write(async () => {
                    await client.call('adConversions.setOn', { on: false });
                    return 'Conversions are off: nothing more goes to Google.';
                  })
                }
              >
                Turn off
              </button>
            ) : (
              <div className="conv-on">
                <label>
                  <span className="flabel">First day</span>
                  <input
                    className="field"
                    type="date"
                    value={first}
                    onChange={(e) => setFirst(e.target.value)}
                  />
                </label>
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={adtrackOff}
                    onChange={(e) => setAdtrackOff(e.target.checked)}
                  />
                  AdTrack’s upload is off
                </label>
                <SaveButton
                  className="btn primary"
                  busy={busy}
                  busyLabel="Turning on…"
                  disabled={!adtrackOff || !first}
                  onClick={() =>
                    void write(async () => {
                      const r = await client.call<{ from: string }>('adConversions.setOn', {
                        on: true,
                        from: first,
                      });
                      return `Conversions are on from ${r.from}.`;
                    })
                  }
                >
                  Turn on
                </SaveButton>
              </div>
            )}
          </li>
          <li className="rule">
            <div className="what">
              <b>Conversion actions</b>
              <p className="muted small">
                Each kind under its own Google Ads conversion action, by click and by call: two kinds on one
                action would count every job twice. A kind with none isn’t sent. {mapFromWords(c.actionsFrom)}
              </p>
              {!draft && (
                <p className="muted small">
                  {KINDS.map((k) => {
                    const a = c.actions[k];
                    const ids = [a.click && `${a.click} by click`, a.call && `${a.call} by call`].filter(
                      Boolean,
                    );
                    return `${KIND_WORDS[k][0]}: ${ids.length ? ids.join(', ') : 'none'}`;
                  }).join('. ')}
                  .
                </p>
              )}
            </div>
            {canEdit && !draft && (
              <button type="button" className="btn" onClick={() => setDraft(draftOf(c.actions))}>
                Change
              </button>
            )}
            {draft && (
              <form
                className="conv-map"
                aria-label="Conversion actions"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (!busy) void saveMap(draft);
                }}
              >
                <ul className="muted small conv-kinds">
                  {KINDS.map((k) => (
                    <li key={k}>
                      {KIND_WORDS[k][0]}: {KIND_WORDS[k][1]}
                    </li>
                  ))}
                </ul>
                <table className="t">
                  <thead>
                    <tr>
                      <th>Kind</th>
                      <th>By click</th>
                      <th>By call</th>
                    </tr>
                  </thead>
                  <tbody>
                    {KINDS.map((k) => (
                      <tr key={k}>
                        <th scope="row">{KIND_WORDS[k][0]}</th>
                        {(['click', 'call'] as const).map((m) => (
                          <td key={m}>
                            <input
                              className="field"
                              inputMode="numeric"
                              aria-label={`${KIND_WORDS[k][0]} by ${m}`}
                              placeholder="None"
                              value={draft[k][m]}
                              onChange={(e) =>
                                setDraft({ ...draft, [k]: { ...draft[k], [m]: e.target.value } })
                              }
                            />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="row">
                  <SaveButton
                    className="btn primary"
                    busy={busy}
                    busyLabel="Saving…"
                    onClick={() => void saveMap(draft)}
                  >
                    Save
                  </SaveButton>
                  <button type="button" className="btn quiet" onClick={() => setDraft(null)}>
                    Cancel
                  </button>
                  {c.actionsFrom === 'setting' && (
                    <button
                      type="button"
                      className="btn quiet"
                      disabled={busy}
                      onClick={() =>
                        void write(async () => {
                          await client.call('settings.set', { key: 'ads.conversionActions', value: null });
                          return 'Conversion actions are the account’s again.';
                        })
                      }
                    >
                      Use the account’s
                    </button>
                  )}
                </div>
              </form>
            )}
          </li>
        </ul>
      )}
      {status && (
        <div className="conv-health" role="group" aria-label="How the ad accounts are doing">
          <h3>How the ad accounts are doing</h3>
          <AdsHealth data={status} host={host} />
        </div>
      )}
    </div>
  );
}
